import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { Toast } from "../../../shared/Toast";
import Loader from "../../../shared/Loader";
import API from "../../../services/api";

const UploadModal = ({ isOpen, onClose, creativeId, creativeType, onUploadSuccess, onUploadError }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fileType, setFileType] = useState(null);
  const dropzoneRef = useRef(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      setFileType(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    setFileType(selectedFile.type.startsWith("image/") ? "image" : "video");
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  useEffect(() => {
    if (isOpen && dropzoneRef.current) dropzoneRef.current.focus();
  }, [isOpen]);

  const onDrop = useCallback((files) => {
    if (!files.length) return Toast("error", "No valid file selected");
    setSelectedFile(files[0]);
  }, []);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.kind !== "file") continue;
      const file = item.getAsFile();
      if (!file) continue;
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) return;
      setSelectedFile(
        new File([file], `clipboard-${Date.now()}.${file.type.split("/")[1]}`, {
          type: file.type,
        })
      );
      e.preventDefault();
      return;
    }
  }, []);

  useEffect(() => {
    if (isOpen) document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [isOpen, handlePaste]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif"],
      "video/*": [".mp4", ".avi", ".mov", ".wmv"],
    },
    multiple: false,
    disabled: uploading,
  });

  const handleUpload = async () => {
    if (!selectedFile) return Toast("error", "Select a file first");
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("files", selectedFile);
      formData.append("creative_id", creativeId);

      let url = creativeType === "half_video" ? (fileType === "image" ? "/upload/alphaImage" : "/upload/alphaVideo") : creativeType.includes("video") ? "/upload/alphaVideo" : "/upload/alphaImage";

      const res = await API({
        url,
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.code === 200 || res.code === 204) {
        Toast("success", res.message || "Upload successful");
        onUploadSuccess?.(res);
        onClose();
        setSelectedFile(null);
      } else {
        Toast("error", res.data?.message || "Upload failed");
        onUploadError?.(res);
      }
    } catch (err) {
      Toast("error", "Upload failed");
      onUploadError?.(err);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between mb-4">
          <h3 className="font-semibold">Upload Design</h3>
          <button onClick={onClose} disabled={uploading}>
            ✕
          </button>
        </div>

        <div
          {...getRootProps()}
          ref={dropzoneRef}
          tabIndex={0}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${isDragActive ? "border-blue-400 bg-blue-50" : isDragReject ? "border-red-400 bg-red-50" : "border-gray-300 hover:bg-gray-50"}`}
        >
          <input {...getInputProps()} />
          {uploading ? <Loader /> : <p>Drag, click, or paste (Ctrl+V)</p>}
        </div>

        {previewUrl && fileType === "image" && <img src={previewUrl} className="mt-4 max-h-48 mx-auto rounded" />}

        {previewUrl && fileType === "video" && <video src={previewUrl} controls className="mt-4 max-h-48 mx-auto rounded" />}

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} disabled={uploading}>
            Cancel
          </button>
          <button onClick={handleUpload} disabled={!selectedFile || uploading} className="bg-blue-600 text-white px-4 py-2 rounded">
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
